import { useAuth } from 'auth';
import AppStoreContext from 'contexts/AppStoreContext';
import { getAppServices } from 'providers/app-services';
import * as React from 'react';
import {
  matchPath,
  useRouteMatch,
  useLocation,
  Redirect,
} from 'react-router-dom';
import { FeatureFlag } from 'featureFlags';
import LoadingAnimation from '../components/LoadingAnimation';
import { EssentialsWebClientApp } from './EssentialsWebClientApp';
import ProjectTemplate from './ProjectTemplate';

const ProjectFallbackRoute = '/project/:projectId' as const;

export const EssentialsWebClientRoutes = {
  Index: '/project/:projectId/test',
  MyWorkspaceEditor: '/my-workspace/:storageId/:fileId/editor',
  MyWorkspace: '/my-workspace/:currentFolderId?',
  DocumentIntegrationEditor:
    '/documents/integrations/:integrationId/document/:documentId/editor',
  DocumentIntegration: '/documents/integrations/:integrationId',
  Documents: '/documents',
  MyDocumentsEditor: '/documents/my-documents/document/:documentId/editor',
  MyDocuments: '/documents/my-documents',
  ProjectDocuments: '/project/:projectId/documents/:currentFolderId?',
  ProjectDocumentEditor: '/project/:projectId/document/:documentId/editor',
  DrawingEditor: '/project/:projectId/drawing/:drawingId/editor',
  StandaloneEditor: '/project/:projectId/drawing/:drawingId/standalone-editor',
  Punches: '/project/:projectId/punches',
  Team: '/project/:projectId/team',
  Users: '/project/:projectId/settings/users',
  Teams: '/project/:projectId/settings/teams',
  Settings: '/project/:projectId/settings',
  Map: '/project/:projectId/map-2',
  Projects: '/projects',
  ConnectedAccounts: '/connected-accounts',
  SessionEditor: '/sessions/:sessionId/document/:documentId/editor',
  Session: '/sessions/:sessionId',
  Sessions: '/sessions',
  StudioProjectEditor:
    '/studio-projects/:studioProjectId/document/:documentId/editor',
  StudioProject: '/studio-projects/:studioProjectId',
  StudioProjects: '/studio-projects',
  Labs: '/labs',
  OrgAdmin: '/org-admin',
} as const;

export const EssentialWebClientRoutesList = [
  EssentialsWebClientRoutes.MyWorkspaceEditor,
  EssentialsWebClientRoutes.MyWorkspace,
  EssentialsWebClientRoutes.ProjectDocuments,
  EssentialsWebClientRoutes.ProjectDocumentEditor,
  EssentialsWebClientRoutes.DrawingEditor,
  EssentialsWebClientRoutes.StandaloneEditor,
  EssentialsWebClientRoutes.Punches,
  EssentialsWebClientRoutes.Index,
  EssentialsWebClientRoutes.Team,
  EssentialsWebClientRoutes.Users,
  EssentialsWebClientRoutes.Teams,
  EssentialsWebClientRoutes.Projects,
  EssentialsWebClientRoutes.Settings,
  EssentialsWebClientRoutes.Map,
  EssentialsWebClientRoutes.ConnectedAccounts,
  EssentialsWebClientRoutes.SessionEditor,
  EssentialsWebClientRoutes.Session,
  EssentialsWebClientRoutes.Sessions,
  EssentialsWebClientRoutes.StudioProject,
  EssentialsWebClientRoutes.StudioProjects,
  EssentialsWebClientRoutes.StudioProjectEditor,
  EssentialsWebClientRoutes.Documents,
  EssentialsWebClientRoutes.DocumentIntegration,
  EssentialsWebClientRoutes.DocumentIntegrationEditor,
  EssentialsWebClientRoutes.MyDocuments,
  EssentialsWebClientRoutes.MyDocumentsEditor,
  EssentialsWebClientRoutes.OrgAdmin,
];

const getEssentialsEnabledRoutes = (): string[] => {
  const { featureFlagService } = getAppServices();

  const labsEnabled = featureFlagService.hasFeature(FeatureFlag.CloudLabs);
  const documentsEnabled = featureFlagService.hasFeature(
    FeatureFlag.CloudDocuments
  );

  const labsRoutes = labsEnabled ? [EssentialsWebClientRoutes.Labs] : [];
  const documentsRoutes = documentsEnabled
    ? [
        EssentialsWebClientRoutes.Documents,
        EssentialsWebClientRoutes.DocumentIntegration,
        EssentialsWebClientRoutes.DocumentIntegrationEditor,
      ]
    : [];

  return [
    EssentialsWebClientRoutes.MyWorkspaceEditor,
    EssentialsWebClientRoutes.MyWorkspace,
    EssentialsWebClientRoutes.ProjectDocuments,
    EssentialsWebClientRoutes.ProjectDocumentEditor,
    EssentialsWebClientRoutes.DrawingEditor,
    EssentialsWebClientRoutes.StandaloneEditor,
    EssentialsWebClientRoutes.Punches,
    EssentialsWebClientRoutes.Session,
    EssentialsWebClientRoutes.Sessions,
    EssentialsWebClientRoutes.SessionEditor,
    EssentialsWebClientRoutes.StudioProject,
    EssentialsWebClientRoutes.StudioProjects,
    EssentialsWebClientRoutes.StudioProjectEditor,
    EssentialsWebClientRoutes.Teams,
    EssentialsWebClientRoutes.Users,
    EssentialsWebClientRoutes.Projects,
    EssentialsWebClientRoutes.Settings,
    EssentialsWebClientRoutes.Map,
    EssentialsWebClientRoutes.ConnectedAccounts,
    EssentialsWebClientRoutes.MyDocuments,
    EssentialsWebClientRoutes.MyDocumentsEditor,
    EssentialsWebClientRoutes.OrgAdmin,
    ...labsRoutes,
    ...documentsRoutes,
  ];
};

export const isEssentialsEnabledWebClientRoute = (
  pathName: string
): boolean => {
  const supportedRouteMatch = getEssentialsEnabledRoutes().some((path) => {
    return matchPath(pathName, {
      path,
    });
  });

  return Boolean(supportedRouteMatch);
};

const getLoadingMessageKey = (path: string): string => {
  if (
    path === EssentialsWebClientRoutes.ProjectDocuments ||
    path === EssentialsWebClientRoutes.Documents ||
    path === EssentialsWebClientRoutes.MyDocuments ||
    path === EssentialsWebClientRoutes.DocumentIntegration
  ) {
    return 'loadingDocuments';
  }

  if (path === EssentialsWebClientRoutes.Sessions) {
    return 'loadingSessions';
  }

  if (
    path === EssentialsWebClientRoutes.ProjectDocumentEditor ||
    path === EssentialsWebClientRoutes.MyDocumentsEditor ||
    path === EssentialsWebClientRoutes.DocumentIntegrationEditor
  ) {
    return 'loadingDocument';
  }

  if (
    path === EssentialsWebClientRoutes.DrawingEditor ||
    path === EssentialsWebClientRoutes.StandaloneEditor
  ) {
    return 'loadingDrawing';
  }

  if (path === EssentialsWebClientRoutes.SessionEditor) {
    return 'loadingSessionDocument';
  }

  if (path === EssentialsWebClientRoutes.Session) {
    return 'loadingSession';
  }

  if (path === EssentialsWebClientRoutes.StudioProjects) {
    return 'loadingStudio';
  }

  return 'loading';
};

const getRouteMetadata = (params: { projectId?: string }, path: string) => {
  return {
    isOnFullScreenRoute:
      path === EssentialsWebClientRoutes.DrawingEditor ||
      path === EssentialsWebClientRoutes.StandaloneEditor ||
      path === EssentialsWebClientRoutes.ProjectDocumentEditor ||
      path === EssentialsWebClientRoutes.SessionEditor ||
      path === EssentialsWebClientRoutes.MyWorkspaceEditor ||
      path === EssentialsWebClientRoutes.MyWorkspace ||
      path === EssentialsWebClientRoutes.ConnectedAccounts ||
      path === EssentialsWebClientRoutes.Sessions ||
      path === EssentialsWebClientRoutes.Session ||
      path === EssentialsWebClientRoutes.StudioProject ||
      path === EssentialsWebClientRoutes.StudioProjects ||
      path === EssentialsWebClientRoutes.StudioProjectEditor ||
      path === EssentialsWebClientRoutes.Projects ||
      path === EssentialsWebClientRoutes.Map ||
      path === EssentialsWebClientRoutes.Labs ||
      path === EssentialsWebClientRoutes.MyDocuments ||
      path === EssentialsWebClientRoutes.MyDocumentsEditor ||
      path === EssentialsWebClientRoutes.DocumentIntegrationEditor ||
      path === EssentialsWebClientRoutes.DocumentIntegration ||
      path === EssentialsWebClientRoutes.Documents ||
      path === EssentialsWebClientRoutes.OrgAdmin,
    isOnProjectFallbackRoute: path === ProjectFallbackRoute,
    isOnProjectRoute: Boolean(params.projectId),
    loadingMessageKey: getLoadingMessageKey(path),
  };
};

/**
 * A renderer that will display the Essentials - Web Client contextually.
 * Namely, if the current route is a project route it will display the application
 * within the project template. Otherwise, the application will be displayed
 * by itself
 */

const EssentialsWebClient: React.VFC = () => {
  const { accessToken, idToken } = useAuth();

  const essentialsWebRoutes = [
    ...getEssentialsEnabledRoutes(),
    ProjectFallbackRoute,
  ];

  const supportedRouteMatch = useRouteMatch<{
    documentId?: string;
    drawingId?: string;
    projectId?: string;
    sessionId?: string;
  }>(essentialsWebRoutes);

  const { isOnFullScreenRoute, isOnProjectFallbackRoute } = getRouteMetadata(
    supportedRouteMatch?.params ?? {},
    supportedRouteMatch?.path ?? ''
  );

  if (supportedRouteMatch === null) {
    return <Redirect to="/my-workspace" />;
  }

  if (isOnFullScreenRoute) {
    return (
      <EssentialsWebClientApp
        credentials={{
          accessToken,
          idToken,
        }}
      />
    );
  }
  /* remove default page padding (p-5) for all pages under Settings section */
  const { pathname } = useLocation();
  const contentPadding = pathname.includes('/settings') ? 'p-0' : null;

  return (
    <>
      <style>{`        
        [data-reach-tabs] {
          display: flex;
          flex-grow: 1;
          flex-direction: column;
        }
        
        [data-reach-tab-panels] {
          display: flex;
          flex-grow: 1;
          flex-direction: column;
        }  
        `}</style>
      <ProjectTemplate
        isHidden={isOnProjectFallbackRoute}
        contentPadding={contentPadding}
      >
        <EssentialsWebClientApp
          credentials={{
            accessToken,
            idToken,
          }}
          projectRoleType={projectRoleType}
        />
      </ProjectTemplate>
    </>
  );
};

export default EssentialsWebClient;
